import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { HeartPulse } from "lucide-react";

function Spinner({ size = "md", className }: { size?: "sm" | "md" | "lg"; className?: string }) {
  const dim = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-10 w-10" : "h-6 w-6";
  return (
    <span
      className={cn("relative inline-flex items-center justify-center text-primary", dim, className)}
      role="status"
      aria-label="Loading"
    >
      <HeartPulse className={cn("motion-safe:animate-pulse", dim)} strokeWidth={2.5} />
      <span
        className={cn(
          "absolute inset-0 rounded-full bg-primary/20 motion-safe:animate-ping opacity-75",
        )}
      />
    </span>
  );
}

export function PageLoader({ label = "Loading", className }: { label?: string; className?: string }) {
  return (
    <div
      className={cn(
        "grid min-h-[60vh] place-items-center motion-safe:animate-fade-in",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-sm text-muted-foreground tracking-wide">{label}</p>
      </div>
    </div>
  );
}

export function FullscreenLoader({ label = "Loading" }: { label?: string }) {
  return <PageLoader label={label} className="min-h-screen" />;
}

export function InlineLoader({
  label = "Loading",
  className,
  size = "sm",
}: {
  label?: string;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <div className={cn("flex items-center gap-2 text-sm text-muted-foreground motion-safe:animate-fade-in", className)}>
      <Spinner size={size} />
      <span>{label}</span>
    </div>
  );
}

export function TableLoaderRow({
  colSpan,
  label = "Loading",
}: {
  colSpan: number;
  label?: string;
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-8">
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Spinner size="sm" />
          <span>{label}</span>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function OverlayLoader({ label = "Loading" }: { label?: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm motion-safe:animate-fade-in">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-sm font-medium text-primary tracking-wide">{label}</p>
      </div>
    </div>
  );
}

export { Spinner };
